import { Attribute, DinerEvent } from 'src/utilities/types';
import { Environment } from 'src/environment';

/**
 * creates a custom event for sending to Consumer Engagement Facade
 *
 * @param eventName custom event name
 * @param brandName capitalized brand name, i.e. "GRUBHUB"
 * @param eventProperties payload of the custom event
 * @returns DinerEvent
 */
export function createDinerCustomEvent(params: {
  eventName: string;
  brandName: string;
  eventProperties: Record<string, any>;
}): DinerEvent {
  const { eventName, eventProperties } = params;

  const customEventAttributes: Attribute[] = Object.keys(eventProperties)
    .map((key) => {
      return {
        name: key,
        value:
          typeof eventProperties[key] === 'string'
            ? eventProperties[key]
            : JSON.stringify(eventProperties[key]),
      };
    })
    // filter out any attributes that don't have a name or value
    .filter((attribute) => attribute.name && attribute.value);

  return {
    event_name: eventName,
    time: Date.now(),
    event_properties: customEventAttributes,
  };
}

/**
 * Per braze updated documentation string event data/properties must be:
 * 1. < 255 characters in length
 * 2. NOT contain $
 * 3. Only contain alphanumeric characters and punctuation
 * @param value value to be sanitized for receiving by braze
 */
export function sanitizeInput(value: any): any {
  const isString = value && typeof value === 'string';
  return isString
    ? value
        .trim()
        .substring(0, 255)
        .replace(/[^a-zA-Z0-9,'"@!*()-_:;`^#/ ]/g, '')
    : value;
}

export enum BRAZE_SDK_URL {
  PROD = 'https://assets.grubhub.com/libs/braze/5.3/braze.min.js',
  PREPROD = 'https://assets-pp.grubhub.com/libs/braze/5.3/braze.min.js',
}

/**
 * Determines the braze sdk url based on the environment
 * @param environment the environment to determine the braze sdk url for
 * @returns the correct url for the braze sdk
 */
export const getBrazeSdkUrl = (environment: string): string => {
  if (environment === Environment.PROD) {
    return BRAZE_SDK_URL.PROD;
  } else {
    return BRAZE_SDK_URL.PREPROD;
  }
};
